// Struttura contenuto delle guide:
// { t: 'p', text }  • { t: 'h2'/'h3', text }  • { t: 'ul'/'ol', items: [] }
// { t: 'step', n, title, body }  • { t: 'code', lang, text }
// { t: 'note'/'warn'/'tip', text }  • { t: 'table', headers, rows }

export const GUIDES = [
  // ─── 1. NUKI ────────────────────────────────────────────────────────────────
  {
    slug: 'nuki-smart-lock',
    title: 'Integrare Nuki Smart Lock con Shangrilux',
    subtitle: 'Configura le serrature Nuki tramite Nuki Web API e webhook per l\'accesso automatico degli ospiti.',
    category: 'Smart Lock',
    brand: 'Nuki',
    tags: ['nuki', 'webhook', 'api', 'smart-lock'],
    difficulty: 'Facile',
    readTime: '10 min',
    publishedAt: '2026-03-30',
    excerpt: 'Guida completa per collegare le serrature Nuki alla piattaforma Shangrilux tramite Nuki Web API e webhook bidirezionali. Automazione completa di check-in e check-out.',
    content: [
      { t: 'note', text: 'Compatibile con Nuki Smart Lock 2.0, 3.0, 3.0 Pro e Ultra. Richiede Nuki Bridge o connettività Wi-Fi integrata (modelli Pro/Ultra).' },
      { t: 'h2', text: 'Prerequisiti' },
      { t: 'ul', items: ['Nuki Smart Lock installato sulla porta (versione 2.0 o superiore)', 'Nuki Bridge (se il modello non ha Wi-Fi integrato)', 'Account Nuki Web gratuito su web.nuki.io', 'Accesso al pannello admin Shangrilux con piano Professional o Elite'] },
      { t: 'h2', text: 'Configurazione Nuki Web API' },
      { t: 'step', n: 1, title: 'Crea un token API su Nuki Web', body: 'Accedi a web.nuki.io → Account → API → Genera token. Seleziona i permessi: "smartlock.readwrite" e "notification.readwrite". Copia il token generato (verrà mostrato una sola volta).' },
      { t: 'step', n: 2, title: 'Recupera l\'ID della serratura', body: 'Nella dashboard Nuki Web, entra nella serratura → Impostazioni → Info avanzate. Copia il "Smartlock ID" numerico (es. 4823749512).' },
      { t: 'step', n: 3, title: 'Configura l\'integrazione in Shangrilux', body: 'Vai su Shangrilux Admin → Impostazioni → Integrazioni Smart Home → Aggiungi dispositivo → Seleziona "Nuki". Inserisci il token API e lo Smartlock ID. Shangrilux verificherà la connessione.' },
      { t: 'step', n: 4, title: 'Configura il Webhook Nuki', body: 'In Nuki Web → Impostazioni Account → Webhook, aggiungi l\'URL:\nhttps://app.shangrilux.com/api/webhook/nuki?slug=IL-TUO-SLUG\nSeleziona gli eventi: "lock", "unlock", "unlatch", "lock_failure". Salva.' },
      { t: 'step', n: 5, title: 'Mappa la serratura alla proprietà', body: 'In Shangrilux Admin → Proprietà → Dispositivi, seleziona la serratura Nuki e associala alla porta principale (o camera specifica). Imposta il comportamento: apertura automatica al check-in, blocco automatico al check-out.' },
      { t: 'step', n: 6, title: 'Configura il codice di accesso temporaneo', body: 'Shangrilux genera automaticamente un Access Code temporaneo per ogni prenotazione confermata e lo invia a Nuki tramite API. Il codice scade automaticamente al check-out.' },
      { t: 'h2', text: 'Payload Webhook Ricevuto da Nuki' },
      { t: 'code', lang: 'json', text: `{
  "nukiId": 4823749512,
  "deviceType": 0,
  "trigger": 2,
  "state": {
    "mode": 2,
    "state": 1,
    "stateName": "locked",
    "batteryCritical": false,
    "timestamp": "2026-03-30T14:30:00+01:00"
  },
  "authId": 1234567
}` },
      { t: 'h2', text: 'Verifica del Funzionamento' },
      { t: 'ol', items: ['In Shangrilux Admin → Integrazioni, il badge Nuki deve mostrare "Connessa" in verde', 'Crea una prenotazione di test con check-in imminente: la serratura deve ricevere un codice PIN temporaneo', 'Verifica in Nuki Web → Registro accessi che l\'apertura sia registrata con il nome dell\'ospite', 'Simula il check-out: verifica che il codice venga revocato automaticamente'] },
      { t: 'h2', text: 'Risoluzione Problemi' },
      { t: 'table', headers: ['Problema', 'Causa probabile', 'Soluzione'], rows: [['Badge "Non connessa"', 'Token API scaduto o revocato', 'Rigenera il token su Nuki Web e aggiorna in Shangrilux'], ['Webhook non ricevuto', 'URL webhook errato o firewall', 'Verifica l\'URL e testa con Nuki Web → Test Webhook'], ['Codice PIN non inviato', 'Nuki Bridge offline', 'Riavvia il Bridge e controlla la connessione Wi-Fi'], ['Serratura non risponde', 'Batterie scariche', 'Sostituisci le batterie (Nuki invia allarme battery critical)'] ] },
      { t: 'tip', text: 'Usa l\'opzione "Auto Unlock" di Nuki per aprire automaticamente la porta al check-in rilevando il telefono dell\'ospite tramite Bluetooth. Richiede che l\'ospite installi l\'app Nuki.' },
    ],
  },

  // ─── 2. TEDEE ────────────────────────────────────────────────────────────────
  {
    slug: 'tedee-smart-lock',
    title: 'Integrare Tedee Pro e Tedee GO con Shangrilux',
    subtitle: 'Collega le serrature Tedee tramite API REST e Bridge Tedee per apertura remota e codici temporanei.',
    category: 'Smart Lock',
    brand: 'Tedee',
    tags: ['tedee', 'api', 'smart-lock', 'webhook'],
    difficulty: 'Facile',
    readTime: '8 min',
    publishedAt: '2026-03-30',
    excerpt: 'Integra Tedee Pro e Tedee GO con Shangrilux in pochi minuti tramite Tedee API. Gestione automatica dei codici PIN e apertura remota per ogni prenotazione.',
    content: [
      { t: 'note', text: 'Compatibile con Tedee Pro e Tedee GO. Richiede Bridge Tedee per la connettività cloud. Tedee GO supporta Bluetooth Low Energy direttamente.' },
      { t: 'h2', text: 'Prerequisiti' },
      { t: 'ul', items: ['Serratura Tedee Pro o GO installata e configurata nell\'app Tedee', 'Bridge Tedee connesso alla rete Wi-Fi (per Tedee Pro)', 'Account Tedee con email verificata', 'API key Tedee (disponibile su api.tedee.com)'] },
      { t: 'h2', text: 'Configurazione' },
      { t: 'step', n: 1, title: 'Ottieni le credenziali API Tedee', body: 'Accedi a api.tedee.com/swagger → Autentica con il tuo account → POST /api/v1.32/account/token. In alternativa, usa il portale Tedee My Account → Integrations → Generate API Key.' },
      { t: 'code', lang: 'bash', text: `curl -X POST https://api.tedee.com/api/v1.32/account/token \\
  -H "Content-Type: application/json" \\
  -d '{"email":"tua@email.it","password":"tua-password"}'
# Risposta: { "result": { "token": "eyJhbGci..." } }` },
      { t: 'step', n: 2, title: 'Recupera l\'ID del dispositivo', body: 'GET https://api.tedee.com/api/v1.32/my/lock → copia il campo "id" dalla risposta JSON per ogni serratura.' },
      { t: 'step', n: 3, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → Tedee → inserisci il bearer token e il Device ID. Shangrilux effettuerà un test di connessione.' },
      { t: 'step', n: 4, title: 'Configura il webhook callback', body: 'In Tedee My Account → Webhooks → Add: URL = https://app.shangrilux.com/api/webhook/tedee?slug=IL-TUO-SLUG. Seleziona eventi: LockStatusChanged, Battery.' },
      { t: 'step', n: 5, title: 'Gestione PIN temporanei', body: 'Shangrilux invia automaticamente un PIN 6 cifre a Tedee per ogni prenotazione confermata tramite POST /api/v1.32/my/lock/{id}/pin. Il PIN scade alla data di check-out.' },
      { t: 'h2', text: 'Payload Stato Serratura' },
      { t: 'code', lang: 'json', text: `{
  "deviceId": 98765,
  "deviceType": 2,
  "state": 6,
  "stateName": "Unlocked",
  "isCharging": false,
  "batteryLevel": 82,
  "timestamp": "2026-03-30T10:15:00Z"
}` },
      { t: 'h2', text: 'Risoluzione Problemi' },
      { t: 'ul', items: ['Token scaduto: i token Tedee hanno validità 24h, Shangrilux li rinnova automaticamente con il refresh token', 'Bridge offline: verifica la connessione Wi-Fi del Bridge Tedee (LED arancione = problema di rete)', 'PIN non accettato: assicurati che il keypad esterno sia abilitato nelle impostazioni Tedee'] },
    ],
  },

  // ─── 3. TTLOCK ────────────────────────────────────────────────────────────────
  {
    slug: 'ttlock-open-api',
    title: 'Integrare Serrature TTLock con Shangrilux',
    subtitle: 'Configura qualsiasi serratura con chip TTLock tramite l\'Open API per gestione centralizzata dei codici ospite.',
    category: 'Smart Lock',
    brand: 'TTLock',
    tags: ['ttlock', 'open-api', 'smart-lock', 'passcode'],
    difficulty: 'Intermedio',
    readTime: '12 min',
    publishedAt: '2026-03-30',
    excerpt: 'TTLock è il chip smart lock più diffuso al mondo (oltre 200 brand). Integra qualsiasi serratura con chip TTLock in Shangrilux tramite l\'Open API ufficiale per gestire PIN, card e fingerprint degli ospiti.',
    content: [
      { t: 'note', text: 'TTLock è un sistema OEM utilizzato da oltre 200 brand di serrature (Ultraloq, Smonet, Teeho, Keyless, ecc.). L\'integrazione funziona con tutti i modelli compatibili TTLock che abbiano un Gateway G2 o G3 connesso.' },
      { t: 'h2', text: 'Prerequisiti' },
      { t: 'ul', items: ['Serratura con chip TTLock installata e configurata nell\'app TTLock', 'Gateway TTLock G2 o G3 connesso alla rete Wi-Fi', 'Account sviluppatore su open.ttlock.com (gratuito)', 'Client ID e Client Secret dell\'applicazione creata'] },
      { t: 'h2', text: 'Registrazione Account Developer' },
      { t: 'step', n: 1, title: 'Crea un\'applicazione API su TTLock Open Platform', body: 'Vai su open.ttlock.com → Registrati con email aziendale → Dashboard → Crea Applicazione. Inserisci nome app, descrizione e callback URL. Otterrai Client ID e Client Secret.' },
      { t: 'step', n: 2, title: 'Trasferisci i dispositivi all\'account developer', body: 'Nell\'app TTLock mobile → Serratura → Condividi → inserisci l\'email del tuo account developer TTLock. I dispositivi appariranno nel portale open.ttlock.com → Devices.' },
      { t: 'step', n: 3, title: 'Ottieni access_token OAuth2', body: 'TTLock usa OAuth2 Password Grant. Chiama l\'endpoint di autenticazione:' },
      { t: 'code', lang: 'bash', text: `curl -X POST https://euapi.ttlock.com/oauth2/token \\
  -d "client_id=TUO_CLIENT_ID" \\
  -d "client_secret=TUO_CLIENT_SECRET" \\
  -d "username=TUA_EMAIL" \\
  -d "password=MD5(TUA_PASSWORD)" \\
  -d "grant_type=password"
# Risposta: { "access_token": "...", "refresh_token": "...", "expires_in": 7776000 }` },
      { t: 'step', n: 4, title: 'Recupera lista serrature', body: 'GET https://euapi.ttlock.com/v3/lock/list?clientId=...&accessToken=...&pageNo=1&pageSize=20&date=TIMESTAMP → copia il "lockId" per ogni serratura da integrare.' },
      { t: 'step', n: 5, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → TTLock → inserisci Client ID, Client Secret, Access Token, Refresh Token. Shangrilux gestirà automaticamente il rinnovo del token alla scadenza (90 giorni).' },
      { t: 'step', n: 6, title: 'Generazione automatica Passcode ospite', body: 'Per ogni prenotazione confermata, Shangrilux chiama POST /v3/keyboardPwd/get per creare un passcode temporaneo con startDate e endDate corrispondenti a check-in e check-out.' },
      { t: 'code', lang: 'json', text: `// Richiesta creazione passcode
{
  "clientId": "CLIENT_ID",
  "accessToken": "ACCESS_TOKEN",
  "lockId": 1234567,
  "keyboardPwdType": 2,
  "keyboardPwdName": "Ospite Mario Rossi",
  "startDate": 1748649600000,
  "endDate": 1748822400000,
  "date": 1748563200000
}
// Risposta: { "keyboardPwdId": 987654, "keyboardPwd": "281945" }` },
      { t: 'h2', text: 'Verifica e Monitoraggio' },
      { t: 'ol', items: ['Controlla in Shangrilux Admin → Dispositivi che la serratura mostri stato "Online"', 'Crea una prenotazione test: verifica che il passcode sia generato e visibile nel log', 'Testa l\'accesso fisicamente con il codice generato', 'Verifica che al check-out il codice venga eliminato (DELETE /v3/keyboardPwd/delete)'] },
      { t: 'warn', text: 'La TTLock Open API EU ha rate limit di 1 richiesta/secondo. Shangrilux gestisce la coda automaticamente. Non creare integrazioni parallele che usino lo stesso token per evitare il ban temporaneo.' },
    ],
  },

  // ─── 4. IGLOOHOME ────────────────────────────────────────────────────────────
  {
    slug: 'igloohome-api',
    title: 'Integrare Igloohome con Shangrilux tramite Iglooworks API',
    subtitle: 'Genera PIN e Access Card temporanee per le serrature Igloohome automaticamente ad ogni prenotazione.',
    category: 'Smart Lock',
    brand: 'Igloohome',
    tags: ['igloohome', 'iglooworks', 'api', 'pin', 'smart-lock'],
    difficulty: 'Facile',
    readTime: '8 min',
    publishedAt: '2026-03-30',
    excerpt: 'Igloohome con tecnologia algoPIN genera codici di accesso temporanei anche senza connessione internet. Integra con Shangrilux per distribuzione automatica dei PIN ad ogni ospite.',
    content: [
      { t: 'note', text: 'Igloohome usa la tecnologia algoPIN™: i codici PIN funzionano anche offline. La serratura calcola il PIN valido basandosi su algoritmo crittografico. Nessun Bridge richiesto per la generazione PIN.' },
      { t: 'h2', text: 'Prerequisiti' },
      { t: 'ul', items: ['Serratura Igloohome (Deadbolt, Padlock, Keybox) installata', 'Account Iglooworks Business su business.igloohome.co', 'API Key Business (richiede piano Business o Enterprise Iglooworks)', 'Numero di serie della serratura'] },
      { t: 'h2', text: 'Configurazione Iglooworks API' },
      { t: 'step', n: 1, title: 'Attiva il piano Business su Iglooworks', body: 'Vai su business.igloohome.co → Registrati → seleziona piano Business. Riceverai l\'API Key nell\'email di benvenuto (formato: igloo_xxxxxxxx).' },
      { t: 'step', n: 2, title: 'Aggiungi le serrature al portale', body: 'Iglooworks Portal → Devices → Add Device → inserisci il numero di serie (QR code sul retro della serratura). Assegna un nome di proprietà per organizzazione.' },
      { t: 'step', n: 3, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → Igloohome → inserisci API Key e seleziona le serrature da gestire.' },
      { t: 'step', n: 4, title: 'Generazione PIN automatica', body: 'Shangrilux chiama l\'API Iglooworks per generare PIN temporanei ad ogni prenotazione confermata. L\'API risponde con il PIN calcolato per l\'intervallo di date specificato.' },
      { t: 'code', lang: 'bash', text: `# Genera PIN temporaneo per ospite
curl -X POST https://api.igloohome.co/v1/locks/{lockId}/pinCodes \\
  -H "Authorization: Bearer igloo_xxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type": "duration",
    "startDateTime": "2026-06-01T14:00:00",
    "endDateTime": "2026-06-05T11:00:00",
    "isActive": true,
    "name": "Ospite Rossi Check-in 01/06"
  }'
# Risposta: { "pinCode": "481627", "id": "pc_abc123" }` },
      { t: 'tip', text: 'I PIN algoPIN di Igloohome funzionano anche senza connessione internet. Ideali per strutture in zone con connettività instabile (montagna, campagna, isole).' },
      { t: 'h2', text: 'Risoluzione Problemi' },
      { t: 'ul', items: ['PIN non funzionante: verifica che l\'orologio della serratura sia sincronizzato (tieni premuto * per 5 secondi)', 'API Key non valida: le key Iglooworks scadono ogni 12 mesi — rigenera dal portale Business', 'Serratura non trovata: verifica il numero di serie e che la serratura sia assegnata all\'account Business'] },
    ],
  },

  // ─── 5. HOME ASSISTANT ───────────────────────────────────────────────────────
  {
    slug: 'home-assistant-integrazione',
    title: 'Integrare Home Assistant con Shangrilux',
    subtitle: 'Usa Home Assistant come hub centralizzato per integrare qualsiasi dispositivo smart home con Shangrilux tramite REST API e webhook.',
    category: 'Smart Hub',
    brand: 'Home Assistant',
    tags: ['home-assistant', 'hass', 'rest-api', 'mqtt', 'webhook', 'automations'],
    difficulty: 'Avanzato',
    readTime: '20 min',
    publishedAt: '2026-03-30',
    excerpt: 'Home Assistant è la piattaforma open source più potente per la domotica. Integra oltre 3.000 dispositivi e collega tutto a Shangrilux tramite REST API e automazioni, senza limiti di brand.',
    content: [
      { t: 'note', text: 'Questa integrazione richiede Home Assistant OS, Supervised o Container. Home Assistant Cloud (Nabu Casa) facilita l\'accesso remoto. Compatibile con HA versione 2024.1 o superiore.' },
      { t: 'h2', text: 'Prerequisiti' },
      { t: 'ul', items: ['Home Assistant installato e accessibile (locale o remoto)', 'Long-Lived Access Token generato in HA', 'Dispositivi già configurati in Home Assistant (serrature, luci, sensori)', 'URL pubblico di HA (tramite Nabu Casa, Nginx Proxy o tunnel)'] },
      { t: 'h2', text: 'Configurazione Home Assistant' },
      { t: 'step', n: 1, title: 'Genera un Long-Lived Access Token', body: 'In HA → Profilo utente (in basso a sinistra) → Sicurezza → Long-Lived Access Tokens → Crea token. Nome: "Shangrilux Integration". Copia il token (mostrato una sola volta).' },
      { t: 'step', n: 2, title: 'Verifica l\'accesso API REST', body: 'Testa la connessione dalla tua macchina:' },
      { t: 'code', lang: 'bash', text: `curl -X GET https://TUO-HA-URL/api/ \\
  -H "Authorization: Bearer TUO_TOKEN" \\
  -H "Content-Type: application/json"
# Deve rispondere: { "message": "API running." }` },
      { t: 'step', n: 3, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → Home Assistant → inserisci URL HA e Access Token. Shangrilux scoprirà automaticamente tutti gli entity_id disponibili.' },
      { t: 'step', n: 4, title: 'Mappa gli entity in Shangrilux', body: 'Per ogni serratura o dispositivo, seleziona il corrispondente entity_id HA (es. lock.porta_principale) e mappa all\'unità in Shangrilux.' },
      { t: 'step', n: 5, title: 'Configura le Automazioni HA per il check-in', body: 'Crea un\'automazione in HA che reagisce al webhook Shangrilux:' },
      { t: 'code', lang: 'yaml', text: `# configuration.yaml — Webhook trigger da Shangrilux
automation:
  - alias: "Shangrilux Check-in Ospite"
    trigger:
      platform: webhook
      webhook_id: shangrilux_checkin_secret_token_123
    action:
      - service: lock.unlock
        target:
          entity_id: lock.porta_principale
      - service: light.turn_on
        target:
          entity_id: light.ingresso
        data:
          brightness_pct: 100
          color_name: warm_white
      - service: climate.set_temperature
        target:
          entity_id: climate.soggiorno
        data:
          temperature: 22` },
      { t: 'step', n: 6, title: 'Configura il webhook di ritorno (stato → Shangrilux)', body: 'In HA, crea un\'automazione che notifica Shangrilux quando lo stato di un dispositivo cambia:' },
      { t: 'code', lang: 'yaml', text: `automation:
  - alias: "Notifica Shangrilux cambio stato serratura"
    trigger:
      platform: state
      entity_id: lock.porta_principale
    action:
      - service: rest_command.shangrilux_notify
        data:
          entity_id: "{{ trigger.entity_id }}"
          state: "{{ trigger.to_state.state }}"
          timestamp: "{{ now().isoformat() }}"

rest_command:
  shangrilux_notify:
    url: "https://app.shangrilux.com/api/webhook/homeassistant?slug=IL-TUO-SLUG"
    method: POST
    headers:
      Authorization: "Bearer OSTIVEX_WEBHOOK_SECRET"
    payload: '{"entity_id": "{{ entity_id }}", "state": "{{ state }}"}'` },
      { t: 'h2', text: 'Integrazioni Dispositivi Consigliati via HA' },
      { t: 'table', headers: ['Dispositivo', 'Integrazione HA', 'Funzione in Shangrilux'], rows: [['Z-Wave Smart Locks', 'Z-Wave JS UI', 'Apertura/chiusura porta'], ['Zigbee Sensors', 'ZHA / Zigbee2MQTT', 'Rilevamento occupazione'], ['Philips Hue', 'Hue Integration', 'Illuminazione check-in'], ['Somfy', 'Somfy integration', 'Tapparelle automatiche'], ['Thermostat Nest/Ecobee', 'Cloud Polling', 'Clima per arrivo ospite']] },
      { t: 'tip', text: 'Usa il componente "template sensor" di HA per esporre valori calcolati (es. numero ospiti attivi, tempo dall\'ultimo accesso) come entity leggibili da Shangrilux via REST.' },
    ],
  },

  // ─── 6. KNX ──────────────────────────────────────────────────────────────────
  {
    slug: 'knx-integrazione',
    title: 'Integrare Impianto KNX con Shangrilux',
    subtitle: 'Collega il bus KNX dell\'edificio a Shangrilux tramite KNX IP Router per controllo centralizzato di accessi, luci e clima.',
    category: 'Building Automation',
    brand: 'KNX',
    tags: ['knx', 'ip-router', 'ets', 'building-automation', 'bus'],
    difficulty: 'Avanzato',
    readTime: '25 min',
    publishedAt: '2026-03-30',
    excerpt: 'KNX è lo standard internazionale per l\'automazione degli edifici. Integra il bus KNX con Shangrilux per automatizzare accessi, illuminazione, clima e tapparelle in strutture alberghiere di alta gamma.',
    content: [
      { t: 'note', text: 'KNX è uno standard ISO/IEC 14543. Questa guida richiede un impianto KNX già cablato e funzionante. La configurazione ETS deve essere eseguita da un installatore KNX certificato.' },
      { t: 'h2', text: 'Prerequisiti' },
      { t: 'ul', items: ['Impianto KNX funzionante con IP Router (es. Weinzierl 730/731, MDT, Siemens N146)', 'ETS6 installato su PC Windows (licenza Professional o superiore)', 'Indirizzi di gruppo (Group Addresses) documentati per luci, serrature, clima, tapparelle', 'Rete LAN che raggiunge sia il KNX IP Router sia il server Shangrilux'] },
      { t: 'h2', text: 'Configurazione KNX IP Router' },
      { t: 'step', n: 1, title: 'Configura IP Routing in ETS6', body: 'In ETS6 → Catalogo → seleziona il tuo IP Router → Configurazione → KNX IP Routing → abilita Multicast. IP Multicast predefinito: 224.0.23.12 / Porta: 3671. Scarica la configurazione sull\'IP Router.' },
      { t: 'step', n: 2, title: 'Documenta i Group Addresses principali', body: 'In ETS6 → Group Addresses, esporta l\'elenco degli indirizzi di gruppo in formato CSV. Categorizza per funzione:' },
      { t: 'table', headers: ['Funzione', 'GA esempio', 'Tipo dato (DPT)'], rows: [['Serratura elettrica porta ingresso ON/OFF', '1/0/1', 'DPT 1.001 (1bit)'], ['Stato serratura feedback', '1/0/2', 'DPT 1.001 (1bit)'], ['Luce corridoio dimmer', '2/0/1', 'DPT 5.001 (0-100%)'], ['Termostato setpoint camera', '3/0/1', 'DPT 9.001 (float)'], ['Tapparella posizione', '4/0/1', 'DPT 5.001 (0-100%)'], ['Presenza camera (sensore IR)', '5/0/1', 'DPT 1.001 (1bit)']] },
      { t: 'step', n: 3, title: 'Configura il driver KNX in Shangrilux', body: 'Admin → Integrazioni → KNX/IP → inserisci IP del router KNX, porta multicast (3671), e individual address assegnato. Shangrilux userà il protocollo KNXnet/IP Routing.' },
      { t: 'step', n: 4, title: 'Mappa i Group Addresses agli oggetti Shangrilux', body: 'Per ogni dispositivo da controllare, inserisci il Group Address e il tipo DPT corrispondente. Shangrilux tradurrà automaticamente i comandi in telegramma KNX.' },
      { t: 'step', n: 5, title: 'Definisci le scene di check-in e check-out', body: 'Crea Scene KNX in Shangrilux (pacchetto multiplo di telegrammi simultanei):' },
      { t: 'code', lang: 'json', text: `{
  "scene_name": "Check-in Camera 101",
  "triggers": ["booking_confirmed", "guest_arrival"],
  "commands": [
    { "ga": "1/0/1", "value": true, "dpt": "1.001" },
    { "ga": "2/0/1", "value": 80, "dpt": "5.001" },
    { "ga": "3/0/1", "value": 21.5, "dpt": "9.001" },
    { "ga": "4/0/1", "value": 50, "dpt": "5.001" }
  ]
}` },
      { t: 'tip', text: 'Usa i "Central Scene" KNX (DPT 18.001) per raggruppare più azioni (accensione luci, temperatura, tapparelle) in un singolo telegramma. Riduce il traffico sul bus e migliora la reattività.' },
      { t: 'warn', text: 'Non configurare mai un Individual Address KNX già in uso da altri dispositivi. Verificare sempre con ETS6 prima di assegnare l\'indirizzo al gateway Shangrilux.' },
    ],
  },

  // ─── 7. BACNET ───────────────────────────────────────────────────────────────
  {
    slug: 'bacnet-ip-integrazione',
    title: 'Integrare Impianti BACnet/IP con Shangrilux',
    subtitle: 'Collega sistemi BACnet/IP (BMS, HVAC, controllo accessi) a Shangrilux per supervisione e automazione avanzata.',
    category: 'Building Automation',
    brand: 'BACnet',
    tags: ['bacnet', 'bms', 'hvac', 'building-automation', 'ip'],
    difficulty: 'Avanzato',
    readTime: '20 min',
    publishedAt: '2026-03-30',
    excerpt: 'BACnet/IP è lo standard ANSI/ASHRAE per la Building Automation. Integra HVAC, controllo accessi e supervisione energetica con Shangrilux tramite il protocollo BACnet/IP.',
    content: [
      { t: 'note', text: 'BACnet/IP richiede un controller BACnet con interfaccia IP (BACnet/IP o BACnet MS/TP con gateway IP). Testato con controller Siemens PXC, Schneider MP-C, Johnson Controls FX, Distech Controls ECY.' },
      { t: 'h2', text: 'Prerequisiti' },
      { t: 'ul', items: ['Controller BACnet con interfaccia IP (indirizzo IP statico raccomandato)', 'Accesso alla rete dove risiede il controller (stessa subnet o routing BACnet/IP)', 'Elenco degli Object Identifier (Device ID, tipo e numero oggetto) da controllare', 'Firewall: porta UDP 47808 aperta tra Shangrilux e il controller'] },
      { t: 'h2', text: 'Configurazione' },
      { t: 'step', n: 1, title: 'Discovery del Device BACnet', body: 'Shangrilux invia un Who-Is broadcast sulla rete per scoprire i device BACnet disponibili. In alternativa, inserisci manualmente l\'indirizzo IP e il Device Instance Number.' },
      { t: 'step', n: 2, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → BACnet/IP → inserisci IP controller, Device Instance Number (tipicamente 1-4194303), porta UDP (47808). Opzionale: BBMD address per routing inter-subnet.' },
      { t: 'step', n: 3, title: 'Leggi e mappa gli Object Properties', body: 'Per ogni point da monitorare o controllare, specifica Object Type e Object Instance:' },
      { t: 'table', headers: ['Object Type', 'Codice', 'Uso tipico in Shangrilux'], rows: [['Analog Input', 'AI', 'Temperatura, umidità, CO2'], ['Analog Output', 'AO', 'Setpoint termostat, valvola'], ['Binary Input', 'BI', 'Stato porta (aperta/chiusa)'], ['Binary Output', 'BO', 'Relè serratura, ventilazione'], ['Analog Value', 'AV', 'Setpoint programmabili'], ['Binary Value', 'BV', 'Flags stato sistema']] },
      { t: 'step', n: 4, title: 'Configura COV Subscriptions', body: 'Shangrilux si iscrive ai Change of Value (COV) per ricevere notifiche in tempo reale quando un valore cambia, senza polling continuo:' },
      { t: 'code', lang: 'json', text: `{
  "service": "SubscribeCOV",
  "device_ip": "192.168.1.50",
  "device_instance": 1001,
  "object_type": "binary-input",
  "object_instance": 5,
  "issue_confirmed_notifications": true,
  "lifetime": 3600,
  "cov_increment": 0
}` },
      { t: 'step', n: 5, title: 'Mappa eventi BACnet ad azioni Shangrilux', body: 'Configura le regole: es. "BI-5 (porta) diventa active → registra accesso in Shangrilux → invia notifica all\'admin".' },
      { t: 'tip', text: 'Per impianti BACnet MS/TP (RS-485), è necessario un gateway BACnet MS/TP ↔ IP come Babel Buster BB3-7301 o ProBACnet. Dopo il gateway, la configurazione è identica a BACnet/IP.' },
    ],
  },

  // ─── 8. WEBHOOK GENERICO ─────────────────────────────────────────────────────
  {
    slug: 'webhook-generico',
    title: 'Integrare Qualsiasi Dispositivo con Webhook Shangrilux',
    subtitle: 'Usa i webhook generici per collegare serrature, sensori, sistemi PMS e qualsiasi piattaforma a Shangrilux.',
    category: 'Webhook',
    brand: 'Generico',
    tags: ['webhook', 'api', 'generic', 'integration', 'hmac'],
    difficulty: 'Facile',
    readTime: '12 min',
    publishedAt: '2026-03-30',
    excerpt: 'I webhook Shangrilux permettono di integrare qualsiasi sistema in grado di fare chiamate HTTP. Scopri come ricevere eventi Shangrilux e inviare stati da dispositivi esterni alla piattaforma.',
    content: [
      { t: 'h2', text: 'Come Funzionano i Webhook' },
      { t: 'p', text: 'Un webhook è una notifica HTTP inviata automaticamente da un sistema a un URL configurato quando accade un evento specifico. Shangrilux supporta webhook sia in entrata (da dispositivi esterni → Shangrilux) sia in uscita (da Shangrilux → sistemi esterni).' },
      { t: 'h2', text: 'Webhook in Uscita (Shangrilux → Sistema Esterno)' },
      { t: 'step', n: 1, title: 'Crea un endpoint ricevitore', body: 'Il tuo sistema deve esporre un endpoint HTTPS pubblico capace di ricevere POST JSON. Il server deve rispondere con HTTP 200 entro 10 secondi.' },
      { t: 'step', n: 2, title: 'Configura il webhook in Shangrilux', body: 'Admin → Integrazioni → Webhook → Nuovo Webhook → inserisci URL, segreto HMAC (opzionale ma raccomandato), e seleziona gli eventi da notificare.' },
      { t: 'table', headers: ['Evento', 'Descrizione', 'Payload chiave'], rows: [['booking.confirmed', 'Prenotazione confermata', 'guest_name, checkin, checkout, access_code'], ['booking.checkin', 'Ospite entrato (check-in effettuato)', 'booking_id, guest_name, room'], ['booking.checkout', 'Ospite uscito (check-out effettuato)', 'booking_id, checkout_time'], ['access.granted', 'Accesso concesso a dispositivo', 'device_id, person, timestamp'], ['access.denied', 'Accesso negato', 'device_id, reason, timestamp'], ['device.offline', 'Dispositivo offline', 'device_id, last_seen']] },
      { t: 'step', n: 3, title: 'Verifica la firma HMAC', body: 'Ogni webhook in uscita include l\'header X-Shangrilux-Signature per verificare l\'autenticità:' },
      { t: 'code', lang: 'python', text: `import hmac, hashlib

def verify_shangrilux_webhook(payload_bytes: bytes, signature: str, secret: str) -> bool:
    expected = hmac.new(
        secret.encode('utf-8'),
        payload_bytes,
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(f"sha256={expected}", signature)

# Nel tuo handler Flask/FastAPI:
# sig = request.headers.get('X-Shangrilux-Signature')
# verify_shangrilux_webhook(request.data, sig, "TUO_SEGRETO")` },
      { t: 'h2', text: 'Webhook in Entrata (Sistema Esterno → Shangrilux)' },
      { t: 'step', n: 4, title: 'Endpoint Shangrilux per eventi dispositivi', body: 'Invia eventi da qualsiasi dispositivo all\'endpoint universale Shangrilux:' },
      { t: 'code', lang: 'bash', text: `# Notifica apertura porta da sistema esterno
curl -X POST https://app.shangrilux.com/api/webhook/generic?slug=IL-TUO-SLUG \\
  -H "Authorization: Bearer TUO_WEBHOOK_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "device_id": "door_main_entrance",
    "event": "access_granted",
    "person_id": "booking_abc123",
    "timestamp": "2026-06-01T14:03:27Z",
    "metadata": {
      "method": "pin",
      "duration_ms": 450
    }
  }'` },
      { t: 'step', n: 5, title: 'Formato payload di risposta Shangrilux', body: 'Shangrilux risponde con JSON contenente il risultato dell\'elaborazione e le azioni eseguite:' },
      { t: 'code', lang: 'json', text: `{
  "received": true,
  "event_id": "evt_k92jx01",
  "booking_matched": true,
  "booking_id": "bk_abc123",
  "actions_triggered": ["log_access", "notify_admin"],
  "timestamp": "2026-06-01T14:03:28Z"
}` },
      { t: 'tip', text: 'Usa ngrok o cloudflared tunnel per testare i webhook durante lo sviluppo senza esporre la rete di produzione. Esempio: ngrok http 8000 → usa l\'URL ngrok come endpoint di test in Shangrilux.' },
    ],
  },

  // ─── 9. PHILIPS HUE ──────────────────────────────────────────────────────────
  {
    slug: 'philips-hue-illuminazione',
    title: 'Automatizzare l\'Illuminazione con Philips Hue e Shangrilux',
    subtitle: 'Crea scene di benvenuto personalizzate, illuminazione adattiva e automazioni check-in/check-out con Philips Hue.',
    category: 'Automazione',
    brand: 'Philips Hue',
    tags: ['philips-hue', 'hue-bridge', 'illuminazione', 'api', 'scene'],
    difficulty: 'Facile',
    readTime: '10 min',
    publishedAt: '2026-03-30',
    excerpt: 'Philips Hue con Shangrilux permette di creare scenari di illuminazione automatici per ogni check-in e check-out. Luce calda al benvenuto, spegnimento automatico alla partenza.',
    content: [
      { t: 'note', text: 'Compatibile con Hue Bridge v2 (square). Hue Bridge v1 (round) non supporta API v2. Richiede firmware 1.60.1960157080 o superiore.' },
      { t: 'h2', text: 'Prerequisiti' },
      { t: 'ul', items: ['Hue Bridge v2 connesso alla rete LAN', 'Luci Philips Hue configurate nell\'app Hue', 'IP address statico del Bridge (imposta in DHCP reservation)', 'App Hue aggiornata all\'ultima versione'] },
      { t: 'h2', text: 'Configurazione' },
      { t: 'step', n: 1, title: 'Scopri il Bridge in rete', body: 'Trova automaticamente l\'IP del tuo Bridge Hue:' },
      { t: 'code', lang: 'bash', text: `# Discovery automatica
curl -s https://discovery.meethue.com
# Risposta: [{"id":"ecb5fafffe...","internalipaddress":"192.168.1.100"}]` },
      { t: 'step', n: 2, title: 'Ottieni il token API (premi il pulsante Bridge)', body: 'Premi fisicamente il bottone sul Bridge Hue, poi entro 30 secondi:' },
      { t: 'code', lang: 'bash', text: `curl -X POST http://192.168.1.100/api \\
  -H "Content-Type: application/json" \\
  -d '{"devicetype":"shangrilux#server","generateclientkey":true}'
# Risposta: [{"success":{"username":"abc123...","clientkey":"DEF456..."}}]` },
      { t: 'step', n: 3, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → Philips Hue → inserisci IP Bridge e username (token API ottenuto al passo 2).' },
      { t: 'step', n: 4, title: 'Crea scene per check-in', body: 'In Shangrilux → Scene → Nuova Scena → "Check-in Benvenuto": seleziona luci, imposta colore (2700K caldo), luminosità 80%, transizione 1s.' },
      { t: 'step', n: 5, title: 'Associa scene agli eventi di prenotazione', body: 'In Shangrilux → Automazioni → Quando: check-in confermato → Allora: attiva scena "Check-in Benvenuto" nella camera corrispondente.' },
      { t: 'code', lang: 'json', text: `// Chiamata Hue API v2 per attivare scena
{
  "recall": {
    "action": "active",
    "duration": 1000,
    "dimming": { "brightness": 80.0 }
  }
}
// PUT https://192.168.1.100/clip/v2/resource/scene/{scene_id}` },
      { t: 'tip', text: 'Usa la funzione "Circadian" di Hue (luce che segue il ciclo giorno/notte) nelle aree comuni dell\'hotel. Migliora il benessere degli ospiti e riduce i consumi energetici notturni.' },
      { t: 'h2', text: 'Scene Raccomandate per Hospitality' },
      { t: 'table', headers: ['Nome Scena', 'Trigger Shangrilux', 'Impostazione Hue'], rows: [['Check-in Benvenuto', 'booking.checkin', '2700K, 80%, fade 1s'], ['Check-out Pulizie', 'booking.checkout', 'Luce Diurna 4000K, 100%'], ['Notte Ospite', 'Orario 23:00', '2200K, 10%, DND attivo'], ['Emergenza', 'Allarme sistema', 'Rosso lampeggiante 100%'], ['Risparmio Energetico', 'Camera vuota 30min', 'Spegnimento automatico']] },
    ],
  },

  // ─── 10. SOMFY ───────────────────────────────────────────────────────────────
  {
    slug: 'somfy-tahoma-tapparelle',
    title: 'Automatizzare Tapparelle e Tende con Somfy TaHoma',
    subtitle: 'Integra i motori Somfy con Shangrilux tramite TaHoma API per automazione di tapparelle, tende e schermature.',
    category: 'Automazione',
    brand: 'Somfy',
    tags: ['somfy', 'tahoma', 'tapparelle', 'tende', 'automazione'],
    difficulty: 'Intermedio',
    readTime: '12 min',
    publishedAt: '2026-03-30',
    excerpt: 'Somfy è leader mondiale nella motorizzazione di tapparelle e tende. Integra TaHoma con Shangrilux per automatizzare le schermature ad ogni check-in, check-out e cambio condizioni meteo.',
    content: [
      { t: 'note', text: 'Compatibile con TaHoma Box, Connexoon, e TaHoma Switch. La TaHoma Local API (senza cloud) richiede firmware recente e permette integrazione diretta senza dipendenze cloud.' },
      { t: 'h2', text: 'Prerequisiti' },
      { t: 'ul', items: ['Box TaHoma o Connexoon configurato con i motori Somfy', 'Account Somfy registrato su somfy.com', 'API Developer access (richiedi su developer.somfy.com)', 'Token OAuth2 Somfy Cloud API'] },
      { t: 'h2', text: 'Configurazione Somfy Cloud API' },
      { t: 'step', n: 1, title: 'Registra un\'applicazione OAuth2', body: 'Vai su developer.somfy.com → My Apps → Create App. Imposta Redirect URI: https://app.shangrilux.com/api/oauth/somfy/callback. Ottieni Consumer Key e Consumer Secret.' },
      { t: 'step', n: 2, title: 'Autorizza l\'accesso all\'account TaHoma', body: 'Shangrilux genererà il link di autorizzazione OAuth2. L\'utente clicca, si autentifica su Somfy, autorizza Shangrilux e viene reindirizzato. Shangrilux salva il token.' },
      { t: 'step', n: 3, title: 'Scopri i dispositivi', body: 'GET https://api.somfy.com/api/v1/site/{siteId}/setup → ritorna lista di tutti i dispositivi Somfy registrati nel TaHoma.' },
      { t: 'step', n: 4, title: 'Configura la TaHoma Local API (opzionale)', body: 'Per evitare dipendenza dal cloud Somfy, usa la Local API:' },
      { t: 'code', lang: 'bash', text: `# Login TaHoma Local API
curl -X POST http://192.168.1.200:8080/enduser-mobile-web/1/enduserAPI/login \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "userId=tua@email.it&userPassword=tua-password"
# Risposta: { "success": true, "roles": {...} }
# Cookie JSESSIONID viene impostato per le richieste successive` },
      { t: 'step', n: 5, title: 'Esegui comandi sui dispositivi', body: 'Invia comandi ai motori tapparelle:' },
      { t: 'code', lang: 'json', text: `// POST /enduser-mobile-web/1/enduserAPI/exec/apply
{
  "label": "Check-in Camera 201 - Alza tapparelle",
  "actions": [
    {
      "deviceURL": "io://1234-5678-9012/12345678",
      "commands": [
        { "name": "setPosition", "parameters": [100] }
      ]
    }
  ]
}` },
      { t: 'h2', text: 'Automazioni Raccomandate per Hospitality' },
      { t: 'ul', items: ['Check-in (ore 14:00+): apri tapparelle al 100% per dare senso di ariosità', 'Check-out: abbassa tapparelle al 50% per privacy durante le pulizie', 'Notte: chiudi automaticamente alle 22:00 se non già chiuse dall\'ospite', 'Meteo (integrazione): chiudi automaticamente in caso di vento forte (>60 km/h) tramite sensore Somfy Sunis'] },
      { t: 'tip', text: 'Usa i "Scenarios" TaHoma per raggruppare più comandi (tapparelle + luci + clima) in un\'unica azione atomica. Riduci il numero di chiamate API e migliora la sincronizzazione.' },
    ],
  },

  // ─── 11. YALE ────────────────────────────────────────────────────────────────
  {
    slug: 'yale-smart-lock',
    title: 'Integrare Yale Smart Lock con Shangrilux',
    subtitle: 'Configura le serrature Yale Conexis, Linus e Assure tramite Yale Access API o Z-Wave via Home Assistant.',
    category: 'Smart Lock',
    brand: 'Yale',
    tags: ['yale', 'conexis', 'linus', 'assure', 'z-wave', 'api'],
    difficulty: 'Intermedio',
    readTime: '15 min',
    publishedAt: '2026-03-30',
    excerpt: 'Yale offre due percorsi di integrazione: API cloud Yale Access per modelli connessi, e Z-Wave per modelli Assure SL via Home Assistant. Questa guida copre entrambi i metodi.',
    content: [
      { t: 'h2', text: 'Scegli il Metodo di Integrazione' },
      { t: 'table', headers: ['Modello Yale', 'Protocollo', 'Metodo consigliato', 'Difficoltà'], rows: [['Yale Conexis L2', 'Bluetooth + Yale Connect Bridge', 'Yale Cloud API', 'Facile'], ['Yale Linus L2', 'Bluetooth + Yale Connect Bridge', 'Yale Cloud API', 'Facile'], ['Yale Assure SL', 'Z-Wave Plus', 'Z-Wave via HA', 'Avanzato'], ['Yale Assure 2', 'Z-Wave / Zigbee', 'Z-Wave via HA', 'Avanzato'], ['Yale Unity', 'Wi-Fi diretto', 'Yale Access API', 'Intermedio']] },
      { t: 'h2', text: 'Metodo A: Yale Access API (Cloud)' },
      { t: 'step', n: 1, title: 'Attiva Yale Connect Bridge', body: 'Il Bridge Yale collegato in rete LAN è necessario per la connettività cloud. Configuralo nell\'app Yale Access. Verifica che la serratura sia "Online" nell\'app.' },
      { t: 'step', n: 2, title: 'Richiedi accesso API Yale', body: 'Yale ha un processo di approvazione per gli sviluppatori. Contatta developer@yalehome.com con il tuo use case (integrazione B2B hospitality). Il processo richiede 3-5 giorni lavorativi.' },
      { t: 'step', n: 3, title: 'OAuth2 e accesso alle serrature', body: 'Una volta approvato, usa il flow OAuth2 per autorizzare Shangrilux ad accedere all\'account Yale del cliente:' },
      { t: 'code', lang: 'bash', text: `# Ottieni lista serrature Yale
curl -X GET https://api.yalehome.com/v1/locks \\
  -H "Authorization: Bearer YALE_ACCESS_TOKEN"
# Risposta: lista serrature con lockId, nome, stato` },
      { t: 'step', n: 4, title: 'Invia codici ospite', body: 'Genera PIN temporanei per ogni prenotazione:' },
      { t: 'code', lang: 'bash', text: `curl -X POST https://api.yalehome.com/v1/locks/{lockId}/pinCodes \\
  -H "Authorization: Bearer YALE_ACCESS_TOKEN" \\
  -d '{"code":"481902","name":"Ospite Bianchi","startTime":"2026-06-01T14:00:00Z","endTime":"2026-06-05T11:00:00Z"}'` },
      { t: 'h2', text: 'Metodo B: Yale Assure via Z-Wave + Home Assistant' },
      { t: 'step', n: 5, title: 'Aggiungi la serratura al controller Z-Wave', body: 'Usa Z-Wave JS UI in Home Assistant. Modalità pairing: tieni premuto il pulsante program della serratura finché il LED lampeggia. La serratura appare come entity "lock.yale_assure_xxx".' },
      { t: 'step', n: 6, title: 'Integra HA con Shangrilux', body: 'Segui la guida Home Assistant (presente nel Blog) per collegare l\'entity Yale ad Shangrilux tramite REST API. Mappa lock.yale_assure_xxx al dispositivo in Shangrilux.' },
    ],
  },

  // ─── 12. SCHLAGE ─────────────────────────────────────────────────────────────
  {
    slug: 'schlage-encode-plus',
    title: 'Integrare Schlage Encode Plus con Shangrilux',
    subtitle: 'Configura Schlage Encode Plus in Wi-Fi diretto per gestire PIN di accesso temporanei via Schlage Home API.',
    category: 'Smart Lock',
    brand: 'Schlage',
    tags: ['schlage', 'encode-plus', 'wifi', 'api', 'access-codes'],
    difficulty: 'Intermedio',
    readTime: '12 min',
    publishedAt: '2026-03-30',
    excerpt: 'Schlage Encode Plus si connette direttamente al Wi-Fi senza hub. Integra con Shangrilux tramite Schlage Home API per gestione automatica dei codici di accesso per ogni ospite.',
    content: [
      { t: 'note', text: 'Schlage Encode Plus richiede Wi-Fi 2.4 GHz (non 5 GHz). Modelli supportati: BE489WB, BE499WB. L\'API Schlage Home è disponibile per partner certificati.' },
      { t: 'h2', text: 'Prerequisiti' },
      { t: 'ul', items: ['Schlage Encode Plus installato e connesso al Wi-Fi 2.4 GHz', 'App Schlage Home configurata con account registrato', 'Accesso API Schlage (richiesta partner access su developer.schlage.com)', 'Massimo 100 access codes per serratura (limite hardware)'] },
      { t: 'h2', text: 'Configurazione' },
      { t: 'step', n: 1, title: 'Registra account developer Schlage', body: 'Visita developer.schlage.com → Partner Program → Submit Application. Indica il caso d\'uso (hospitality management platform). Schlage fornisce sandbox credentials entro 5-7 giorni.' },
      { t: 'step', n: 2, title: 'Autenticazione OAuth2', body: 'Schlage usa OAuth2 con PKCE. Shangrilux gestisce il flow automaticamente dopo l\'inserimento delle credenziali partner.' },
      { t: 'step', n: 3, title: 'Scopri dispositivi e serrature', body: 'GET /devices → ritorna lista serrature Schlage collegate all\'account con deviceId, modello e stato.' },
      { t: 'step', n: 4, title: 'Gestione Access Codes', body: 'Crea codici di accesso temporanei per ogni prenotazione:' },
      { t: 'code', lang: 'bash', text: `# Crea access code temporaneo
curl -X POST https://api.schlage.com/v1/devices/{deviceId}/accessCodes \\
  -H "Authorization: Bearer ACCESS_TOKEN" \\
  -d '{
    "name": "Ospite Verdi 01/06",
    "code": "3847",
    "codeType": "scheduled",
    "schedule": {
      "startTime": "2026-06-01T14:00:00-05:00",
      "endTime": "2026-06-05T11:00:00-05:00"
    }
  }'` },
      { t: 'step', n: 5, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → Schlage → inserisci Client ID, Client Secret. Dopo autorizzazione OAuth, seleziona le serrature da gestire. Shangrilux creerà e revocherà i codici automaticamente.' },
      { t: 'h2', text: 'Limitazioni e Best Practice' },
      { t: 'ul', items: ['Limite di 100 codici per serratura: Shangrilux pulisce automaticamente i codici scaduti prima di crearne di nuovi', 'Codici 4-8 cifre: non usare combinazioni ovvie (1234, 0000)', 'Schlage Encode Plus non supporta carte RFID: solo PIN numerico', 'Per strutture con alta rotazione ospiti: usa codici unici per ogni prenotazione (non riusare PIN)'] },
      { t: 'warn', text: 'Schlage Encode Plus memorizza i codici localmente nella serratura. In caso di serratura offline (Wi-Fi giù), i codici già programmati continuano a funzionare, ma non è possibile aggiungerne di nuovi.' },
    ],
  },
];

export const CATEGORIES = ['Tutti', 'Smart Lock', 'Smart Hub', 'Building Automation', 'Automazione', 'Webhook', 'MQTT'];

// ─── 13. DORMAKABA ───────────────────────────────────────────────────────────
const GUIDE_DORMAKABA = {
  slug: 'dormakaba-integrazione',
  title: 'Integrare Dormakaba con Shangrilux',
  subtitle: 'Collega i sistemi di controllo accessi Dormakaba (EVOline, Ambiance, Silca) con Shangrilux tramite OSDP v2 e REST API hospitality.',
  category: 'Smart Lock',
  brand: 'Dormakaba',
  tags: ['dormakaba', 'osdp', 'access-control', 'hotel', 'rfid'],
  difficulty: 'Avanzato',
  readTime: '18 min',
  publishedAt: '2026-03-30',
  excerpt: 'Dormakaba è leader mondiale nei sistemi di controllo accessi per hospitality. Integra EVOline, Ambiance e Silca con Shangrilux tramite OSDP v2, API REST e protocollo hospitality per gestione chiavi card e PIN.',
  content: [
    { t: 'note', text: 'Dormakaba include i brand storici Dorma, Kaba, Legic, Ilco, Silca. Questa guida copre i sistemi hotel della linea EVOline/Ambiance e i controller OSDP v2. Per sistemi legacy RS-232, contatta il supporto tecnico Dormakaba.' },
    { t: 'h2', text: 'Prerequisiti' },
    { t: 'ul', items: ['Controller Dormakaba EVOline o Ambiance con firmware 5.x o superiore', 'Modulo IP Gateway Dormakaba (art. 97100) per connettività REST', 'Credenziali account Dormakaba Partner Portal', 'Software Dormakaba Configuration Manager installato (Windows)', 'Rete LAN con accesso al controller (IP statico raccomandato)'] },
    { t: 'h2', text: 'Architettura del Sistema' },
    { t: 'p', text: 'I sistemi Dormakaba per hotel usano una topologia a stella: il controller centrale gestisce reader/lock via bus RS-485 (OSDP v2). Il modulo IP Gateway espone un\'API REST che Shangrilux usa per emettere credenziali e leggere eventi.' },
    { t: 'table', headers: ['Componente', 'Ruolo', 'Protocollo'], rows: [['Controller EVOline', 'Hub centrale, gestisce fino a 128 porte', 'RS-485 OSDP v2'], ['IP Gateway Dormakaba', 'Ponte da RS-485 a Ethernet/REST', 'HTTP/HTTPS + JSON'], ['Reader RFID (MIFARE DESFire)', 'Lettura card ospite', 'ISO 14443-A'], ['Cilindro elettronico Kaba', 'Accesso camere e aree comuni', 'Bluetooth (Legic Advant)'], ['Kaba EloStar / EloStar Plus', 'Stanza hotel con display', 'RS-485']] },
    { t: 'h2', text: 'Configurazione OSDP v2' },
    { t: 'step', n: 1, title: 'Abilita OSDP v2 sul Controller', body: 'In Dormakaba Configuration Manager → Controller Settings → Communication → imposta Protocol: OSDP v2. Abilita SC (Secure Channel) per comunicazioni crittografate. Genera e distribuisci le chiavi SCBK ai reader.' },
    { t: 'step', n: 2, title: 'Configura IP Gateway REST', body: 'Connetti il Gateway IP alla rete LAN. Accedi al pannello web (http://192.168.x.x) → API Settings → REST API → Enable. Genera API Key. Configura TLS se disponibile nel firmware.' },
    { t: 'step', n: 3, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → Dormakaba → inserisci IP Gateway, porta API (default 8080), API Key e Site Code (codice sito a 4 cifre assegnato dal controller). Shangrilux verificherà la connessione.' },
    { t: 'step', n: 4, title: 'Configura il Card Format', body: 'Dormakaba usa formati card proprietari (Legic Advant, MIFARE DESFire). In Shangrilux specifica il formato card emesso: HID 26-bit, HID 37-bit, o Legic UID.' },
    { t: 'step', n: 5, title: 'Emissione credenziali ospite', body: 'Per ogni prenotazione confermata, Shangrilux chiama l\'API Dormakaba per creare una credenziale temporanea:' },
    { t: 'code', lang: 'json', text: `// POST /api/v1/credentials
{
  "type": "mobile_key",
  "holder": {
    "name": "Mario Rossi",
    "reference": "booking_id_abc123"
  },
  "validity": {
    "from": "2026-06-01T14:00:00Z",
    "to": "2026-06-05T11:00:00Z"
  },
  "access_groups": ["hotel_rooms", "pool", "gym"],
  "room_numbers": ["101"]
}` },
    { t: 'step', n: 6, title: 'Mobile Key via Bluetooth', body: 'Dormakaba supporta Mobile Key tramite app Kaba Mobile. Shangrilux invia il link di invito all\'ospite via email. L\'ospite installa l\'app, riceve la chiave digitale e apre la porta tramite Bluetooth senza contatto fisico.' },
    { t: 'tip', text: 'Usa i "Time Zones" Dormakaba per limitare l\'accesso degli ospiti alle aree comuni solo negli orari di apertura (es. SPA 08:00-22:00). Le Time Zone si configurano nel Controller e vengono associate agli Access Groups.' },
    { t: 'warn', text: 'I sistemi Dormakaba richiedono certificazione installatore per la configurazione iniziale (EloStar, OSDP pairing). L\'integrazione API può essere effettuata autonomamente solo dopo che il sistema base è già operativo.' },
  ],
};

// ─── 14. SALTO ───────────────────────────────────────────────────────────────
const GUIDE_SALTO = {
  slug: 'salto-ks-api',
  title: 'Integrare Salto KS e Salto SHIP con Shangrilux',
  subtitle: 'Collega serrature Salto XS4 e ARIA con Shangrilux tramite Salto KS Cloud API o SHIP (Salto Hospitality Integration Protocol).',
  category: 'Smart Lock',
  brand: 'Salto',
  tags: ['salto', 'salto-ks', 'ship', 'xs4', 'aria', 'rfid', 'access-control'],
  difficulty: 'Intermedio',
  readTime: '15 min',
  publishedAt: '2026-03-30',
  excerpt: 'Salto Systems è uno dei principali brand di controllo accessi per hotel. Integra XS4, ARIA e NEO con Shangrilux tramite Salto KS Cloud API per gestione centralizata di chiavi digitali e PIN.',
  content: [
    { t: 'note', text: 'Salto offre due percorsi: Salto KS (cloud-based, abbonamento mensile) e Salto SHIP (on-premise, integrazione server-to-server). Questa guida copre Salto KS per la maggior parte delle installazioni hospitality.' },
    { t: 'h2', text: 'Prerequisiti' },
    { t: 'ul', items: ['Dispositivi Salto XS4, ARIA o NEO installati e configurati nel portale Salto KS', 'Account Salto KS con piano Business o superiore', 'API Key Salto KS (da Developer Portal)', 'Per Salto SHIP: server on-premise con SHIPS software installato'] },
    { t: 'h2', text: 'Metodo A: Salto KS Cloud API' },
    { t: 'step', n: 1, title: 'Accedi al Salto KS Developer Portal', body: 'Vai su developer.saltosystems.com → registra un\'applicazione partner → ottieni client_id e client_secret. Il tier gratuito include 100 device. Richiedi il tier hospitality per volumi maggiori.' },
    { t: 'step', n: 2, title: 'Autenticazione OAuth2', body: 'Salto KS usa OAuth2 Client Credentials:' },
    { t: 'code', lang: 'bash', text: `curl -X POST https://api.salto-ks.com/oauth2/token \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=client_credentials" \\
  -d "client_id=TUO_CLIENT_ID" \\
  -d "client_secret=TUO_SECRET" \\
  -d "scope=device.read device.write key.write event.read"
# Risposta: { "access_token": "eyJ...", "expires_in": 3600 }` },
    { t: 'step', n: 3, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → Salto KS → inserisci Client ID e Secret. Shangrilux gestirà automaticamente il token refresh.' },
    { t: 'step', n: 4, title: 'Lista Dispositivi', body: 'GET https://api.salto-ks.com/v2/devices?type=lock → ritorna serrature con device_id, nome, stato batteria e connettività.' },
    { t: 'step', n: 5, title: 'Emissione Mobile Key', body: 'Per ogni prenotazione, Shangrilux crea una Mobile Key Salto con finestra temporale:' },
    { t: 'code', lang: 'json', text: `// POST https://api.salto-ks.com/v2/keys
{
  "device_ids": ["lock_abc", "lock_xyz"],
  "name": "Ospite Mario Rossi — Camera 201",
  "valid_from": "2026-06-01T14:00:00Z",
  "valid_until": "2026-06-05T11:00:00Z",
  "key_type": "mobile",
  "delivery": {
    "method": "email",
    "email": "ospite@email.com"
  }
}
// Risposta: { "key_id": "key_123", "qr_code_url": "https://..." }` },
    { t: 'step', n: 6, title: 'Webhook eventi accesso', body: 'Configura webhook in Salto KS → Settings → Webhooks → aggiungi URL Shangrilux per eventi: key_used, key_rejected, battery_low.' },
    { t: 'h2', text: 'Metodo B: Salto SHIP (On-Premise)' },
    { t: 'p', text: 'Per installazioni con server on-premise, Salto offre il protocollo SHIP (Salto Hospitality Integration Protocol). Il server SHIPS espone un\'API SOAP/REST locale. Contatta Salto per la documentazione SHIP 3.0 e le credenziali di accesso partner.' },
    { t: 'tip', text: 'Salto offre "Virtual Network" per collegare serrature offline (batteria) tramite smartphone degli ospiti. Ogni volta che l\'ospite apre l\'app Salto, la serratura riceve aggiornamenti programma senza connessione Wi-Fi.' },
  ],
};

// ─── 15. AUGUST ──────────────────────────────────────────────────────────────
const GUIDE_AUGUST = {
  slug: 'august-smart-lock',
  title: 'Integrare August Smart Lock con Shangrilux',
  subtitle: 'Configura August Wi-Fi Smart Lock e August Pro tramite August API per gestione PIN ospiti e apertura remota.',
  category: 'Smart Lock',
  brand: 'August',
  tags: ['august', 'smart-lock', 'wifi', 'api', 'yale', 'pin'],
  difficulty: 'Facile',
  readTime: '10 min',
  publishedAt: '2026-03-30',
  excerpt: 'August (brand del gruppo Yale/Assa Abloy) offre serrature Wi-Fi dirette senza hub. Integra August Smart Lock in Shangrilux per generare PIN ospiti automatici e monitorare gli accessi in tempo reale.',
  content: [
    { t: 'note', text: 'August Wi-Fi Smart Lock (4a generazione) si connette direttamente al Wi-Fi 2.4 GHz senza bridge aggiuntivo. August Pro e Smart Lock 3a gen richiedono August Connect Wi-Fi Bridge. Questa integrazione usa le August Connect API.' },
    { t: 'h2', text: 'Prerequisiti' },
    { t: 'ul', items: ['August Smart Lock installato e configurato nell\'app August Home', 'August Connect Wi-Fi Bridge collegato (per modelli senza Wi-Fi integrato)', 'Account August Developer (developer.august.com)', 'API Key approvata (richiede approvazione manuale August)'] },
    { t: 'h2', text: 'Configurazione' },
    { t: 'step', n: 1, title: 'Registra applicazione API August', body: 'Accedi a developer.august.com → Request API Access. Compila il modulo con: nome app "Shangrilux", use case "hospitality management", numero di serrature gestite. August risponde entro 3-5 giorni lavorativi con le credenziali.' },
    { t: 'step', n: 2, title: 'Autenticazione e recupero serrature', body: 'August usa un flow di autenticazione email + validazione codice:' },
    { t: 'code', lang: 'bash', text: `# Step 1: Invia codice di verifica all'email
curl -X POST https://api-production.august.com/session \\
  -H "Accept-Version: 0.0.1" \\
  -H "x-august-api-key: TUA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"password":"tua-password","email":"tua@email.com","phone":null}'

# Step 2: Verifica il codice ricevuto via email
curl -X POST https://api-production.august.com/validate/email \\
  -H "x-august-api-key: TUA_API_KEY" \\
  -H "x-august-access-token: TOKEN_DA_STEP1" \\
  -d '{"code":"123456"}'` },
    { t: 'step', n: 3, title: 'Lista serrature', body: 'GET /locks → ritorna tutte le serrature associate all\'account con lockId, nome, stato (locked/unlocked) e livello batteria.' },
    { t: 'step', n: 4, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → August → inserisci API Key e token di sessione. Shangrilux mapperà automaticamente le serrature disponibili.' },
    { t: 'step', n: 5, title: 'Gestione Guest Access Codes', body: 'Crea codici pin temporanei per gli ospiti:' },
    { t: 'code', lang: 'bash', text: `# Crea access code temporaneo
curl -X POST https://api-production.august.com/lock/{lockId}/accesscode \\
  -H "x-august-api-key: TUA_API_KEY" \\
  -H "x-august-access-token: TUO_TOKEN" \\
  -d '{
    "name": "Ospite Bianchi",
    "code": "4729",
    "startTime": "2026-06-01 14:00:00",
    "endTime": "2026-06-05 11:00:00",
    "accesscodeType": "all_day_recurring",
    "duration": 30
  }'` },
    { t: 'h2', text: 'Apertura Remota' },
    { t: 'step', n: 6, title: 'Remote unlock per check-in digitale', body: 'Shangrilux può aprire la serratura da remoto al check-in confermato: PUT /lock/{lockId}/remoteoperate con payload {"status":"unlock"}. La serratura si richiude automaticamente dopo il timeout configurato.' },
    { t: 'tip', text: 'August supporta "Activity Feed" in tempo reale: ogni evento (apertura, chiusura, codice inserito) è disponibile tramite GET /lock/{lockId}/activities. Shangrilux importa questi eventi per il log accessi della struttura.' },
  ],
};

// ─── 16. ASSA ABLOY ──────────────────────────────────────────────────────────
const GUIDE_ASSAABLOY = {
  slug: 'assa-abloy-aperio',
  title: 'Integrare Assa Abloy Aperio e Vaka con Shangrilux',
  subtitle: 'Collega sistemi Assa Abloy Aperio (wireless) e Vaka Connect con Shangrilux per controllo accessi hotel enterprise.',
  category: 'Smart Lock',
  brand: 'Assa Abloy',
  tags: ['assa-abloy', 'aperio', 'vaka', 'abloy-pulse', 'wireless', 'hotel'],
  difficulty: 'Avanzato',
  readTime: '20 min',
  publishedAt: '2026-03-30',
  excerpt: 'Assa Abloy (gruppo che include Yale, HID, Abloy, Mul-T-Lock) è il leader mondiale nel controllo accessi. Integra Aperio wireless e Vaka Connect con Shangrilux per sistemi hotel di massima sicurezza.',
  content: [
    { t: 'note', text: 'Il gruppo Assa Abloy include: Yale, HID Global, Abloy, Mul-T-Lock, Medeco, Sargent, ASSA, Adams Rite. Questa guida copre Aperio (sistema wireless) e Vaka (piattaforma cloud hospitality). Per Yale, consulta la guida dedicata.' },
    { t: 'h2', text: 'Prerequisiti' },
    { t: 'ul', items: ['Hardware Aperio: Communication Hub (AH40) o online controller ACX/C10 con modulo Aperio', 'Firmware ACX aggiornato (versione 3.x o superiore per REST API)', 'Licenza Aperio IP Gateway Software', 'Per Vaka: account Assa Abloy Hospitality Operations, contratto partner', 'Certificato SSL per comunicazioni sicure'] },
    { t: 'h2', text: 'Architettura Aperio Wireless' },
    { t: 'p', text: 'Aperio usa 2,4 GHz wireless proprietario. I lock comunicano con gli hub AH40 (raggio 20-30m), che a loro volta si connettono all\'ACX Controller via Ethernet. Il controller espone API REST per Shangrilux.' },
    { t: 'step', n: 1, title: 'Configura l\'ACX Controller', body: 'Accedi all\'interfaccia web ACX (https://IP-ACX/acx-web/) → Configuration → REST API → Enable. Crea un account API con ruolo "Integration". Annota API Key e URL base.' },
    { t: 'step', n: 2, title: 'Discovery dispositivi Aperio', body: 'GET /api/v2/doors → ritorna lista porte con door_id, nome, stato (locked/unlocked/unknown), livello batteria wireless.' },
    { t: 'step', n: 3, title: 'Configura in Shangrilux', body: 'Admin → Integrazioni → Assa Abloy Aperio → inserisci URL ACX, API Key, Site ID. Seleziona le porte da gestire.' },
    { t: 'step', n: 4, title: 'Gestione credenziali MIFARE DESFire', body: 'Aperio usa card MIFARE DESFire EV3. Shangrilux genera credenziali tramite HID OEM encoding o direttamente tramite ACX:' },
    { t: 'code', lang: 'json', text: `// POST /api/v2/credentials — Crea credenziale ospite
{
  "type": "card",
  "format": "mifare_desfire",
  "holder": {
    "name": "Ospite Verdi",
    "external_id": "booking_xyz789"
  },
  "access_rules": [
    { "door_id": "door_101", "schedule": "hotel_guest" },
    { "door_id": "door_pool", "schedule": "pool_hours" }
  ],
  "valid_from": "2026-06-01T14:00:00Z",
  "valid_until": "2026-06-05T11:00:00Z"
}` },
    { t: 'step', n: 5, title: 'Mobile Key via HID Mobile Access', body: 'Assa Abloy offre HID Mobile Access per chiavi digitali Bluetooth. L\'ospite riceve un invito email, installa HID Mobile Access, e apre le porte con lo smartphone o Apple Watch.' },
    { t: 'h2', text: 'Piattaforma Vaka Connect (Hospitality Cloud)' },
    { t: 'p', text: 'Vaka Connect è la piattaforma cloud Assa Abloy dedicata all\'hospitality. Offre PMS integration nativa, mobile key, keyless check-in e reporting avanzato. L\'integrazione con Shangrilux avviene tramite webhook bidirezionali.' },
    { t: 'warn', text: 'L\'integrazione Aperio richiede che il firmware ACX sia aggiornato. Controller con firmware 2.x non supportano REST API. Contatta il tuo rivenditore Assa Abloy per l\'aggiornamento firmware prima di procedere.' },
  ],
};

// ─── 17. MQTT STANDALONE ─────────────────────────────────────────────────────
const GUIDE_MQTT = {
  slug: 'mqtt-standalone',
  title: 'Integrare Dispositivi MQTT con Shangrilux',
  subtitle: 'Connetti qualsiasi dispositivo IoT con supporto MQTT a Shangrilux tramite broker MQTT (Mosquitto, EMQX, HiveMQ).',
  category: 'MQTT',
  brand: 'MQTT',
  tags: ['mqtt', 'mosquitto', 'emqx', 'iot', 'broker', 'publish-subscribe'],
  difficulty: 'Intermedio',
  readTime: '15 min',
  publishedAt: '2026-03-30',
  excerpt: 'MQTT è il protocollo IoT più diffuso al mondo. Integra qualsiasi serratura, sensore o attuatore MQTT-enabled con Shangrilux tramite broker Mosquitto o EMQX, senza codice personalizzato.',
  content: [
    { t: 'h2', text: 'Cos\'è MQTT e Perché Usarlo' },
    { t: 'p', text: 'MQTT (Message Queuing Telemetry Transport) è un protocollo publish-subscribe leggero per dispositivi IoT, standardizzato ISO/IEC 20922. Ogni dispositivo pubblica messaggi su "topic" (es. hotel/room101/lock/state) e il broker li distribuisce ai subscriber (come Shangrilux).' },
    { t: 'h2', text: 'Prerequisiti' },
    { t: 'ul', items: ['Broker MQTT installato: Mosquitto (open source) o EMQX/HiveMQ (enterprise)', 'Dispositivi IoT con client MQTT configurati (serrature, sensori, termostati)', 'URL broker MQTT accessibile da Shangrilux (cloud o VPN)', 'Autenticazione TLS raccomandata (certificato X.509)'] },
    { t: 'h2', text: 'Installazione Broker Mosquitto' },
    { t: 'code', lang: 'bash', text: `# Installazione su Ubuntu/Debian
sudo apt-get install -y mosquitto mosquitto-clients

# Configurazione base /etc/mosquitto/mosquitto.conf
listener 8883
protocol mqtt
cafile /etc/mosquitto/certs/ca.crt
certfile /etc/mosquitto/certs/server.crt
keyfile /etc/mosquitto/certs/server.key
allow_anonymous false
password_file /etc/mosquitto/passwd

# Crea utente Shangrilux
mosquitto_passwd -c /etc/mosquitto/passwd shangrilux
# → inserisci password sicura

sudo systemctl enable mosquitto && sudo systemctl start mosquitto` },
    { t: 'h2', text: 'Configurazione in Shangrilux' },
    { t: 'step', n: 1, title: 'Configura connessione broker', body: 'Admin → Integrazioni → MQTT → inserisci: Broker URL (mqtts://tuo-broker.io), Porta (8883 per TLS, 1883 senza), Username: shangrilux, Password, e (opzionale) Client Certificate per mutual TLS.' },
    { t: 'step', n: 2, title: 'Definisci la struttura dei Topic', body: 'Shangrilux si iscrive ai topic secondo il pattern configurato. Struttura raccomandata:' },
    { t: 'code', lang: 'bash', text: `# Struttura topic MQTT per hotel
hotel/{proprieta}/{stanza}/lock/state       # Stato serratura (locked/unlocked)
hotel/{proprieta}/{stanza}/lock/command     # Comandi (lock/unlock)
hotel/{proprieta}/{stanza}/sensor/door      # Sensore porta (open/closed)
hotel/{proprieta}/{stanza}/climate/temp     # Temperatura attuale
hotel/{proprieta}/{stanza}/climate/setpoint # Temperatura target
hotel/{proprieta}/common/alarm              # Allarme struttura

# Esempio topic camera 101 di shangrilux:
hotel/shangrilux/room101/lock/state
hotel/shangrilux/room101/lock/command` },
    { t: 'step', n: 3, title: 'Formato payload JSON', body: 'I messaggi MQTT verso Shangrilux devono rispettare il formato:' },
    { t: 'code', lang: 'json', text: `// Pubblicato da serratura su: hotel/shangrilux/room101/lock/state
{
  "state": "locked",
  "battery_level": 75,
  "signal_strength": -68,
  "last_access_by": "booking_abc123",
  "timestamp": "2026-06-01T14:03:27Z"
}` },
    { t: 'step', n: 4, title: 'Shangrilux pubblica comandi ai dispositivi', body: 'Quando un ospite fa check-in, Shangrilux pubblica su hotel/shangrilux/room101/lock/command:' },
    { t: 'code', lang: 'json', text: `{
  "command": "unlock",
  "authorized_by": "shangrilux_booking_abc123",
  "duration_seconds": 10,
  "timestamp": "2026-06-01T14:00:00Z",
  "hmac": "sha256_firma_del_messaggio"
}` },
    { t: 'step', n: 5, title: 'QoS e Retain Messages', body: 'Configura la Quality of Service appropriata: QoS 0 per log (at most once), QoS 1 per comandi (at least once), QoS 2 per eventi critici (exactly once). Usa retain=true per lo stato corrente della serratura.' },
    { t: 'tip', text: 'Usa EMQX Enterprise per installazioni con più di 50 dispositivi MQTT. Offre clustering, rule engine, dashboard e integrazione nativa con database. È gratuito fino a 10.000 connessioni simultanee.' },
  ],
};

// ─── 18. ZIGBEE2MQTT ─────────────────────────────────────────────────────────
const GUIDE_ZIGBEE2MQTT = {
  slug: 'zigbee2mqtt-integrazione',
  title: 'Integrare Dispositivi Zigbee con Shangrilux tramite Zigbee2MQTT',
  subtitle: 'Usa Zigbee2MQTT per collegare serrature, sensori e attuatori Zigbee a Shangrilux tramite broker MQTT locale.',
  category: 'MQTT',
  brand: 'Zigbee2MQTT',
  tags: ['zigbee', 'zigbee2mqtt', 'sonoff', 'aqara', 'mqtt', 'open-source', 'hue'],
  difficulty: 'Avanzato',
  readTime: '22 min',
  publishedAt: '2026-03-30',
  excerpt: 'Zigbee2MQTT converte qualsiasi dispositivo Zigbee in messaggi MQTT, senza hub proprietari. Supporta 3.000+ dispositivi: serrature, sensori porte, termostati, luci. Ideale per integrazioni cost-effective senza abbonamenti cloud.',
  content: [
    { t: 'note', text: 'Zigbee2MQTT supporta oltre 3.000 dispositivi da 400+ brand (Aqara, IKEA, Philips Hue, Sonoff, Tuya, Xiaomi, Bosch, ecc.). Consulta zigbee2mqtt.io/supported-devices per verificare compatibilità del tuo dispositivo.' },
    { t: 'h2', text: 'Prerequisiti' },
    { t: 'ul', items: ['Coordinatore Zigbee USB: Sonoff Zigbee 3.0 Dongle Plus (raccomandato), CC2652R, ConBee II o Phoscon', 'Raspberry Pi 4 o server Linux per ospitare Zigbee2MQTT (Docker raccomandato)', 'Broker Mosquitto installato (vedi guida MQTT)', 'Node.js 20+ o Docker Engine', 'Dispositivi Zigbee compatibili (serrature, sensori, luci)'] },
    { t: 'h2', text: 'Installazione Zigbee2MQTT' },
    { t: 'step', n: 1, title: 'Installazione con Docker Compose (raccomandato)', body: 'Crea il file docker-compose.yml:' },
    { t: 'code', lang: 'yaml', text: `version: '3.8'
services:
  zigbee2mqtt:
    image: koenkk/zigbee2mqtt:latest
    container_name: zigbee2mqtt
    restart: unless-stopped
    volumes:
      - ./z2m-data:/app/data
      - /run/udev:/run/udev:ro
    ports:
      - "8080:8080"
    devices:
      - /dev/ttyUSB0:/dev/ttyUSB0  # dongle Sonoff
    environment:
      - TZ=Europe/Rome` },
    { t: 'step', n: 2, title: 'Configura configuration.yaml', body: 'Crea ./z2m-data/configuration.yaml:' },
    { t: 'code', lang: 'yaml', text: `homeassistant: false
permit_join: false   # cambia in true per aggiungere dispositivi
mqtt:
  base_topic: zigbee2mqtt
  server: mqtt://192.168.1.200:1883
  user: shangrilux
  password: TUA_PASSWORD_MQTT
serial:
  port: /dev/ttyUSB0
  adapter: deconz   # o ember per Sonoff
frontend:
  port: 8080
  auth_token: TOKEN_SICURO_FRONTEND
advanced:
  log_level: info
  pan_id: GENERATE
  channel: 25` },
    { t: 'step', n: 3, title: 'Aggiungi dispositivi Zigbee', body: 'Abilita temporaneamente il pairing: in Zigbee2MQTT Frontend (http://192.168.1.x:8080) → clicca "Allow join (All)" → metti il dispositivo in modalità pairing (procedura diversa per ogni brand — consulta zigbee2mqtt.io). Il dispositivo appare entro 60 secondi.' },
    { t: 'step', n: 4, title: 'Configura le serrature Zigbee', body: 'Le serrature Zigbee (es. Aqara N200, Sonoff ZBMINIL2, Danalock V3) pubblicano automaticamente su:' },
    { t: 'code', lang: 'bash', text: `# Topic auto-generati da Zigbee2MQTT
zigbee2mqtt/Serratura_Camera_101        # Stato completo (JSON)
zigbee2mqtt/Serratura_Camera_101/set    # Comandi
zigbee2mqtt/Serratura_Camera_101/get    # Richiesta stato

# Esempio payload ricevuto da Aqara N200:
{
  "state": "LOCK",
  "battery": 82,
  "door_state": "CLOSED",
  "lock_state": "LOCKED",
  "sound_volume": "HIGH",
  "linkquality": 98
}` },
    { t: 'step', n: 5, title: 'Collega Zigbee2MQTT a Shangrilux', body: 'In Shangrilux Admin → Integrazioni → MQTT → configura il broker Mosquitto. Poi in Integrazioni → Zigbee2MQTT → imposta base_topic (zigbee2mqtt) e mappa ogni dispositivo all\'unità/camera corrispondente.' },
    { t: 'step', n: 6, title: 'Manda comandi alla serratura', body: 'Shangrilux pubblica comandi sul topic /set:' },
    { t: 'code', lang: 'bash', text: `# Apri serratura (publish su mosquitto)
mosquitto_pub -h 192.168.1.200 -t "zigbee2mqtt/Serratura_Camera_101/set" \\
  -m '{"state": "UNLOCK"}' -u shangrilux -P password

# Risposta asincrona su topic principale:
# { "state": "UNLOCK", "linkquality": 95, ... }` },
    { t: 'h2', text: 'Dispositivi Zigbee Raccomandati per Hospitality' },
    { t: 'table', headers: ['Dispositivo', 'Brand', 'Funzione', 'Note'], rows: [['N200', 'Aqara', 'Serratura + sensore porta + lettore NFC', 'Molto stabile, raccomandato'], ['ZBMINIL2', 'Sonoff', 'Relè per serrature elettromeccaniche', 'Economico, max 10A'], ['Danalock V3', 'Danalock', 'Serratura cilindro europea Z-Wave + Zigbee', 'Dual protocol'], ['WSDCGQ11LM', 'Aqara', 'Sensore T/H per clima camera', 'Precisione ±0.2°C'], ['JTQJ-BF-01LM', 'Aqara', 'Sensore gas/fumo', 'Obbligatorio in cucine'], ['E1524/E1743', 'IKEA', 'Luci smart a basso costo', 'Zigbee 3.0']] },
    { t: 'warn', text: 'Zigbee usa la banda 2,4 GHz come Wi-Fi e Bluetooth. In ambienti con molti dispositivi Wi-Fi, usa il canale Zigbee 25 o 26 per minimizzare le interferenze. Non mettere il dongle Zigbee direttamente sulla porta USB del server — usa un\'estensione USB da almeno 50cm.' },
  ],
};

// ── Append to GUIDES array ──────────────────────────────────────────────────
GUIDES.push(GUIDE_DORMAKABA, GUIDE_SALTO, GUIDE_AUGUST, GUIDE_ASSAABLOY, GUIDE_MQTT, GUIDE_ZIGBEE2MQTT);


export const DIFFICULTY_COLOR = {
  'Facile':     { bg: 'rgba(5,150,105,0.12)',  text: '#059669', border: 'rgba(5,150,105,0.3)' },
  'Intermedio': { bg: 'rgba(201,168,76,0.12)', text: '#c9a84c', border: 'rgba(201,168,76,0.3)' },
  'Avanzato':   { bg: 'rgba(239,68,68,0.1)',   text: '#ef4444', border: 'rgba(239,68,68,0.25)' },
};

export const CATEGORY_COLOR = {
  'Smart Lock':          '#c9a84c',
  'Smart Hub':           '#6366f1',
  'Building Automation': '#a78bfa',
  'Automazione':         '#06b6d4',
  'Webhook':             '#10b981',
  'Generico':            '#94a3b8',
};
